import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** 后端 role_key, 例如 common / lawyer */
export type Role = string
export type UserSex = '0' | '1' | '2'

interface UserInfo {
  userId?: string
  username?: string
  nickname?: string
  avatar?: string
  phonenumber?: string
  email?: string
  sex?: UserSex
}

interface AuthState {
  accessToken: string | null
  expiresAt: number | null
  /** 正在从登录身份切换为游客身份，用于阻止退出期间触发游客请求 */
  isLoggingOut: boolean
  /** 当前账号具备的角色集合 (可能多个) */
  roles: Role[]
  userInfo: UserInfo | null

  setAuth: (token: string, expireIn: number) => void
  setRoles: (roles: Role[]) => void
  setUserInfo: (info: UserInfo | null) => void
  patchUserInfo: (info: Partial<UserInfo>) => void
  isAuthenticated: () => boolean
  /** 外部登录回调前仅清理旧认证，不触发游客会话清理 */
  prepareForLogin: () => void
  logout: () => void
  finishLogout: () => void
}

/**
 * 鉴权状态 store
 *
 * 持久化到 localStorage(legal-web:auth), 含 token / 角色 / 用户信息.
 * 登录: setAuth + setRoles + setUserInfo
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      expiresAt: null,
      isLoggingOut: false,
      roles: [],
      userInfo: null,

      setAuth: (token, expireIn) =>
        set({
          accessToken: token,
          expiresAt: Date.now() + expireIn * 1000,
          isLoggingOut: false,
        }),

      setRoles: (roles) => set({ roles }),

      setUserInfo: (info) => set({ userInfo: info }),

      patchUserInfo: (info) =>
        set((state) => ({
          userInfo: state.userInfo ? { ...state.userInfo, ...info } : state.userInfo,
        })),

      isAuthenticated: () => {
        const { accessToken, expiresAt } = get()
        if (!accessToken) return false
        if (expiresAt && Date.now() > expiresAt) return false
        return true
      },

      prepareForLogin: () =>
        set({
          accessToken: null,
          expiresAt: null,
          isLoggingOut: false,
          roles: [],
          userInfo: null,
        }),

      logout: () =>
        set({
          accessToken: null,
          expiresAt: null,
          isLoggingOut: true,
          roles: [],
          userInfo: null,
        }),

      finishLogout: () => set({ isLoggingOut: false }),
    }),
    {
      name: 'legal-web:auth',
      partialize: (s) => ({
        accessToken: s.accessToken,
        expiresAt: s.expiresAt,
        roles: s.roles,
        userInfo: s.userInfo,
      }),
    },
  ),
)
